import { useEffect, useState } from "react";
import axios from "helpers/api";

/**
 * Drug schedule tokens.
 *
 * Source of truth is GET /inventory/schedule-tokens. The list below is only a
 * fallback used until that call returns (or if it fails), so the schedule
 * pickers never render empty.
 */
export const FALLBACK_SCHEDULE_TOKENS = [
  { token: "2", label: "Schedule II" },
  { token: "3", label: "Schedule III" },
  { token: "4", label: "Schedule IV" },
  { token: "5", label: "Schedule V" },
  { token: "otc", label: "OTC" },
  { token: "-999", label: "Non-controlled / Other" }
];

// Short label used where space is tight (table chips, tiles).
const SHORT_LABEL = {
  "2": "C 2",
  "3": "C 3",
  "4": "C 4",
  "5": "C 5",
  otc: "OTC",
  "-999": "Non Controlled"
};

export const shortLabelFor = (token) => SHORT_LABEL[String(token).toLowerCase()] || String(token);

/**
 * Turns a medication's raw schedule fields into one of the tokens above, using
 * the same rule as the Drug Schedule report:
 *   drug_schedule 2–5            -> "2" … "5" (controlled)
 *   otherwise drug_subtype "O"   -> "otc"
 *   otherwise                    -> "-999" (non-controlled — the API sends 0)
 * A missing drug_schedule stays "" (shown as "—") instead of being guessed.
 */
const ROMAN = { ii: "2", iii: "3", iv: "4", v: "5" };

export const scheduleTokenOf = (schedule, subtype) => {
  const raw = String(schedule ?? "").trim().toLowerCase();
  if (raw === "otc" || raw === "-999") return raw;

  // 2, "2", "2N", "C2", "C-II"
  const controlled = raw.match(/^(?:c-?)?(ii|iii|iv|v|[2-5])n?$/);
  if (controlled) return ROMAN[controlled[1]] || controlled[1];

  const sub = String(subtype ?? "").trim().toLowerCase();
  if (sub === "o" || sub === "otc") return "otc";

  return raw === "" ? "" : "-999";
};

let cachedTokens = null;
let inFlight = null;

const authHeaders = () => {
  const tokenStr = localStorage.getItem("accessToken");
  return tokenStr ? { Authorization: `Bearer ${tokenStr}` } : {};
};

const apiUrl = (path) => {
  const baseUrl = process.env.REACT_APP_SERVER_URL || "";
  return baseUrl ? `${baseUrl}${path}` : path;
};

/** Fetches once per page load and shares the result between callers. */
export const fetchScheduleTokens = () => {
  if (cachedTokens) return Promise.resolve(cachedTokens);
  if (inFlight) return inFlight;

  inFlight = axios
    .get(apiUrl("/inventory/schedule-tokens"), { headers: authHeaders() })
    .then((res) => {
      const list = res.data?.data;
      if (Array.isArray(list) && list.length > 0) {
        cachedTokens = list
          .filter((t) => t && t.token !== undefined && t.token !== null)
          .map((t) => ({ token: String(t.token), label: t.label || String(t.token) }));
      } else {
        cachedTokens = FALLBACK_SCHEDULE_TOKENS;
      }
      return cachedTokens;
    })
    .catch((err) => {
      console.warn("Schedule tokens fetch warning:", err?.message);
      cachedTokens = FALLBACK_SCHEDULE_TOKENS;
      return cachedTokens;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/**
 * Returns the schedule tokens plus label helpers.
 * Renders with the fallback list immediately, then swaps in the API list.
 */
const useScheduleTokens = () => {
  const [tokens, setTokens] = useState(cachedTokens || FALLBACK_SCHEDULE_TOKENS);
  const [loaded, setLoaded] = useState(Boolean(cachedTokens));

  useEffect(() => {
    let cancelled = false;
    fetchScheduleTokens().then((list) => {
      if (!cancelled) {
        setTokens(list);
        setLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const labelOf = (token) => {
    const key = String(token).toLowerCase();
    const hit = tokens.find((t) => String(t.token).toLowerCase() === key);
    return hit ? hit.label : String(token);
  };

  return { tokens, loaded, labelOf, shortLabelOf: shortLabelFor };
};

export default useScheduleTokens;
